import i18n from "i18next";
import { SingleSalesLeadViewApi } from "src/api/generated/erp/sales/crm/salesLeads/api/singleSalesLeadViewApi.ts";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { AavoMosaic } from "src/components/common/mosaic/AavoMosaic.tsx";
import { SalesLeadInfoView } from "src/components/views/erp/sales/crm/salesLeads/SalesLeadInfoView.tsx";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { ObjectCommentingView } from "src/components/views/erp/common/objectComments/ObjectCommentingView.tsx";
import { SalesLeadActivitiesDataGrid } from "src/components/views/erp/sales/crm/salesLeadActivities/SalesLeadActivitiesDataGrid.tsx";
import { SalesLeadOffersDataGrid } from "src/components/views/erp/sales/crm/salesLeads/SalesLeadOffersDataGrid.tsx";
import { SalesLeadCustomerOrdersView } from "src/components/views/erp/sales/crm/salesLeads/SalesLeadCustomerOrdersView.tsx";
import { SalesLeadDocumentsView } from "src/components/views/erp/sales/crm/salesLeads/SalesLeadDocumentsView.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { ContactsDataGrid } from "src/components/views/erp/contacts/ContactsDataGrid.tsx";
import { getContactsGridKey } from "src/components/views/erp/sales/crm/salesLeads/salesLeadContactsGridKey.ts";

export interface SingleSalesLeadViewProps {
	salesLeadId: number;
}

export const SingleSalesLeadView = ({ salesLeadId }: SingleSalesLeadViewProps) => {
	const { tenantConfig } = useTenantCustomizations();
	const hasPermission = useHasPermission();
	const leadEditable = hasPermission(Permissions.Sales.SalesLeads.MANAGE);

	return (
		<AsyncFetchRender
			fetch={() =>
				SingleSalesLeadViewApi.getSingleSalesLeadViewData({
					salesLeadId: salesLeadId,
				})
			}
			content={({ lead, attributes }, reload) => (
				<AavoMosaic
					viewId={"B5C2E8A140F391D6"}
					layout={{
						type: "row",
						splitPercentage: 30,
						first: {
							type: "panel",
							content: (
								<SalesLeadInfoView
									lead={lead}
									attributes={attributes}
									refreshData={async () => {
										await reload();
									}}
								/>
							),
						},
						second: {
							type: "tabs",
							items: [
								{
									type: "panel",
									title: i18n.t("comments"),
									content: (
										<ObjectCommentingView
											key={lead.salesLeadId}
											objectType={"SALES_LEAD"}
											objectId={lead.salesLeadId}
											editable={leadEditable}
										/>
									),
								},
								{
									type: "panel",
									title: i18n.t("sales_lead_activities_tab"),
									content: (
										<SalesLeadActivitiesDataGrid
											key={lead.salesLeadId}
											salesLeadId={lead.salesLeadId}
											onActivitiesChanged={() => reload()}
										/>
									),
								},
								{
									type: "panel",
									title: i18n.t("contacts"),
									content: (
										<ContactsDataGrid
											// Lead form saves may change the primary contact, which reloads the contacts.
											key={getContactsGridKey(lead)}
											connectedItemType={"SALES_LEAD"}
											connectedItemId={lead.salesLeadId}
											editable={leadEditable}
											onContactsChanged={() => reload()}
										/>
									),
								},
								tenantConfig.documentsEnabled && {
									type: "panel",
									title: i18n.t("documents"),
									content: (
										<SalesLeadDocumentsView
											key={lead.salesLeadId}
											salesLeadId={lead.salesLeadId}
											hidePreview
										/>
									),
								},
								{
									type: "panel",
									title: i18n.t("offers"),
									content: (
										<SalesLeadOffersDataGrid
											key={lead.salesLeadId}
											salesLeadId={lead.salesLeadId}
										/>
									),
								},
								{
									type: "panel",
									title: i18n.t("customer_orders"),
									content: (
										<SalesLeadCustomerOrdersView
											key={lead.salesLeadId}
											salesLeadId={lead.salesLeadId}
										/>
									),
								},
							],
						},
					}}
				/>
			)}
		/>
	);
};
